import type { ReactNode } from 'react';
import { Button } from '@immo/ui-kit';
import type { FleetCommandView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { CommandOutcomeBadge } from './command-outcome-badge';
import { commandStep } from './command-steps';

type Props = {
  /** Where the command runs: the machine, and what it was narrowed to. */
  readonly scope: string;
  /** What it runs, under the scope line — `run-command`'s words. */
  readonly subject?: ReactNode;
  readonly command: FleetCommandView;
  /** What the settled command carried back; shown only once it settled. */
  readonly children: ReactNode;
};

/**
 * The second phase of a form verb's dialog (`run-command`, `e2e-unlock`, `system-preview`): the issued command,
 * followed through the same poll as the feed (sent, received, running) until it settles. Closing never cancels —
 * the command keeps running on the host and settles in the feed.
 *
 * The body and footer only, never the `Modal`: each dialog keeps one `Modal` across both phases, so the dialog
 * stays the same element when the form gives way to the command it issued.
 */
export function FollowedCommand({ scope, subject, command, children }: Props) {
  const t = useTranslations('pages.admin.fleet.run-command');
  const settled = command.outcome !== 'accepted';

  return (
    <>
      <p className="font-mono text-base-sm text-text-secondary">{scope}</p>
      {subject !== undefined && <p className="mt-2 break-words font-mono text-base-sm text-text-primary">{subject}</p>}
      <div className="mt-3 flex">
        <CommandOutcomeBadge outcome={command.outcome} step={commandStep(command)} detail={command.detail} />
      </div>
      {settled ? (
        <div className="mt-3 flex flex-col gap-2">
          {command.detail !== null && (
            <p className="break-words text-sm text-text-secondary" data-testid="followed-command-detail">
              {command.detail}
            </p>
          )}
          {children}
        </div>
      ) : (
        <p className="mt-3 text-sm text-text-tertiary">{t('running-hint')}</p>
      )}
    </>
  );
}

/** Close, and — once the command settled — back to the form for another. */
export function FollowedCommandFooter({
  command,
  againLabel,
  onAgain,
  onClose,
}: {
  readonly command: FleetCommandView;
  readonly againLabel: string;
  /** Back to the form, with what was typed still in it. */
  readonly onAgain: () => void;
  readonly onClose: () => void;
}) {
  const t = useTranslations('pages.admin.fleet.run-command');

  return (
    <>
      {command.outcome !== 'accepted' && (
        <Button variant="secondary" type="button" onClick={onAgain}>
          {againLabel}
        </Button>
      )}
      <Button variant="ghost" type="button" onClick={onClose}>
        {t('close-cta')}
      </Button>
    </>
  );
}

/** The tail of what a command printed, in a scrolling mono block, saying when earlier output was cut. */
export function CommandOutput({ output, truncated }: { readonly output: string; readonly truncated: boolean }) {
  const t = useTranslations('pages.admin.fleet.run-command');

  return (
    <>
      {truncated && <p className="text-sm text-warning-700">{t('truncated')}</p>}
      {output === '' ? (
        <p className="text-sm text-text-tertiary">{t('no-output')}</p>
      ) : (
        <pre
          aria-label={t('output')}
          className="max-h-[320px] overflow-auto whitespace-pre rounded-sm bg-neutral-900 p-3 font-mono text-xs text-neutral-50 selection:bg-neutral-50/30 sm:max-h-[420px] sm:text-sm"
        >
          {output}
        </pre>
      )}
    </>
  );
}
